import axios from 'axios'
import { getUrl } from '@/common/helper'

// Decode JWT payload and check if the exp claim is in the past
function isTokenExpired (token) {
  if (!token) return true
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return !payload.exp || payload.exp < Date.now() / 1000
  } catch (e) {
    return true
  }
}

// Shared refresh promise — concurrent requests all wait on the same call
let refreshPromise = null

function doRefresh () {
  if (!refreshPromise) {
    refreshPromise = axios.post(getUrl('core', 'authentication/refresh-token'), {
      AccessToken: localStorage.getItem('token'),
      RefreshToken: localStorage.getItem('refreshToken')
    })
      .then(({ data }) => {
        localStorage.setItem('token', data.accessToken)
        localStorage.setItem('refreshToken', data.refreshToken)
        return data.accessToken
      })
      .catch(err => {
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        window.location.href = '/login'
        throw err
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

export default (withCredentials = false) => {
  const instance = axios.create({
    withCredentials: withCredentials,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  })

  // Proactive: refresh before sending if token is already expired
  instance.interceptors.request.use(async config => {
    const token = localStorage.getItem('token')
    const refreshToken = localStorage.getItem('refreshToken')

    if (token && isTokenExpired(token) && refreshToken) {
      const newToken = await doRefresh()
      config.headers['Authorization'] = 'Bearer ' + newToken
    } else if (token) {
      config.headers['Authorization'] = 'Bearer ' + token
    }

    return config
  })

  // Reactive: catch 401 for clock-skew cases where token looked valid client-side
  instance.interceptors.response.use(
    response => response,
    async error => {
      const original = error.config

      if (error.response && error.response.status === 401 && !original._retry) {
        original._retry = true
        const refreshToken = localStorage.getItem('refreshToken')

        if (!refreshToken) {
          localStorage.removeItem('token')
          window.location.href = '/login'
          return Promise.reject(error)
        }

        try {
          const newToken = await doRefresh()
          original.headers['Authorization'] = 'Bearer ' + newToken
          return axios(original)
        } catch (err) {
          return Promise.reject(err)
        }
      }

      return Promise.reject(error)
    }
  )

  return instance
}
